import { z } from 'zod';
import {
  DISCIPLINES,
  LEVEL_PATTERN,
  LIGHTING_VARIANTS,
  MASTER_PARTS,
  UNIT_PATTERN,
  type Discipline,
  type LightingVariant,
} from './naming.ts';

/**
 * `public/assets/manifest.json` — the asset contract, written by whichever tier
 * generated the geometry and read by `StreamingManager` at runtime.
 *
 * The point of the manifest is that **tier swaps are a no-op**. T0 placeholders,
 * T1 parametric geometry and T2 baked output all emit the same node names, the
 * same model-set grouping and the same manifest shape. Replacing one GLB and
 * re-running `validate-manifest` is the entire migration.
 */

export const ASSET_TIERS = ['T0', 'T1', 'T2'] as const;
export type AssetTier = (typeof ASSET_TIERS)[number];

export const NodeAssetSchema = z.strictObject({
  /** Canonical `{UNIT}_{LEVEL}_{DISCIPLINE}`. */
  name: z.string(),
  unit: z.string().regex(UNIT_PATTERN),
  level: z.string().regex(LEVEL_PATTERN),
  discipline: z.enum(DISCIPLINES),
  /** Path relative to `basePath`. Carries the lighting variant if any. */
  file: z.string().min(1),
  bytes: z.number().int().nonnegative(),
  variant: z.enum(LIGHTING_VARIANTS).nullable(),
});

export const MasterAssetSchema = z.strictObject({
  part: z.enum(MASTER_PARTS),
  name: z.string(),
  file: z.string().min(1),
  bytes: z.number().int().nonnegative(),
  variant: z.enum(LIGHTING_VARIANTS).nullable(),
});

export const ModelSetSchema = z.strictObject({
  /** The unit whose geometry was authored. Others instance it. */
  representativeUnit: z.string().regex(UNIT_PATTERN),
  /** Every unit that resolves to this set. */
  units: z.array(z.string().regex(UNIT_PATTERN)).min(1),
  /** Canonical node names belonging to the representative unit. */
  nodes: z.array(z.string()).min(1),
});

export const ManifestSchema = z.strictObject({
  version: z.literal(1),
  tier: z.enum(ASSET_TIERS),
  generator: z.string().min(1),
  generatedAt: z.string().min(1),
  /** URL prefix the files are served from. */
  basePath: z.string().min(1),
  /** True when every node asset ships day and night variants. */
  hasLightingVariants: z.boolean(),
  nodes: z.array(NodeAssetSchema),
  master: z.array(MasterAssetSchema),
  modelSets: z.record(z.string(), ModelSetSchema),
  /** Room-label anchors, emitted by T1 and T2. Absent at T0. */
  annotations: z.string().min(1).nullable(),
});

export type NodeAsset = z.infer<typeof NodeAssetSchema>;
export type MasterAsset = z.infer<typeof MasterAssetSchema>;
export type ModelSet = z.infer<typeof ModelSetSchema>;
export type Manifest = z.infer<typeof ManifestSchema>;

// ---------------------------------------------------------------------------
// Annotations — room labels, emitted alongside geometry by T1/T2
// ---------------------------------------------------------------------------

export const AnnotationSchema = z.strictObject({
  id: z.string().min(1),
  unit: z.string().regex(UNIT_PATTERN),
  level: z.string().regex(LEVEL_PATTERN),
  labelAr: z.string().min(1),
  /** m², computed from the room footprint. */
  area: z.number().positive().nullable(),
  /** Model-local metres. */
  position: z.tuple([z.number(), z.number(), z.number()]),
});

export const AnnotationsFileSchema = z.strictObject({
  version: z.literal(1),
  generator: z.string().min(1),
  items: z.array(AnnotationSchema),
});

export type Annotation = z.infer<typeof AnnotationSchema>;
export type AnnotationsFile = z.infer<typeof AnnotationsFileSchema>;

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export interface AssetQuery {
  unit?: string;
  level?: string;
  discipline?: Discipline;
  variant?: LightingVariant | null;
}

export function selectNodeAssets(
  manifest: Manifest,
  query: AssetQuery,
): NodeAsset[] {
  return manifest.nodes.filter((asset) => {
    if (query.unit !== undefined && asset.unit !== query.unit) return false;
    if (query.level !== undefined && asset.level !== query.level) return false;
    if (query.discipline !== undefined && asset.discipline !== query.discipline)
      return false;
    if (query.variant !== undefined && asset.variant !== query.variant)
      return false;
    return true;
  });
}

/** Resolve which set's geometry to fetch for a unit, and under which name. */
export function resolveModelSet(
  manifest: Manifest,
  unitId: string,
): { setId: string; set: ModelSet } | undefined {
  for (const [setId, set] of Object.entries(manifest.modelSets)) {
    if (set.units.includes(unitId)) return { setId, set };
  }
  return undefined;
}

export function assetUrl(manifest: Manifest, file: string): string {
  const base = manifest.basePath.endsWith('/')
    ? manifest.basePath
    : `${manifest.basePath}/`;
  return `${base}${file}`;
}

export function totalBytes(assets: ReadonlyArray<{ bytes: number }>): number {
  return assets.reduce((sum, asset) => sum + asset.bytes, 0);
}
